import {
  Box,
  Button,
  CircularProgress,
  Popover,
  Typography,
} from '@mui/material';
import {
  Download,
  Eye,
  File,
  Plus,
  Trash,
  Upload,
} from '@phosphor-icons/react';
import React, { useId, useRef } from 'react';
import FilesModal from '../../LayoutComponents/Uokiads/FileModal/FilesModal';
import { CollapsibleFolderGroup } from './CollapsibleFolderGroup';
import { Item } from './Item';
import { useDataSidebarContext } from './context';
import { initialPreview, useUploadItems } from './hooks/useUploadItems';

interface UploadItemsProps {
  spaceId?: string;
}

export const UploadItems: React.FC<UploadItemsProps> = ({ spaceId }) => {
  const { isDarkMode, palette } = useDataSidebarContext();
  const reactId = useId();
  const uploadFileRef = useRef<HTMLInputElement>(null);

  const textColor = isDarkMode ? palette.gray[300] : palette.gray[600];
  const iconColor = isDarkMode ? palette.gray[400] : palette.gray[500];

  const {
    docs,
    activeDoc,
    isUploading,
    preview,
    anchorEl,
    setPreview,
    handleUpload,
    handleDelete,
    handlePreview,
    handleDownload,
    closePopover,
    openPopover,
  } = useUploadItems(spaceId);

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    uploadFileRef.current?.click();
  };

  const addButton = (
    <Box
      component='button'
      onClick={handleAddClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        color: iconColor,
        '&:hover': { color: textColor },
      }}
    >
      <Plus size={14} weight='bold' />
    </Box>
  );

  const popoverButtonSx = {
    width: '100%',
    background: 'unset',
    justifyContent: 'flex-start',
    textTransform: 'unset' as const,
    fontSize: '14px',
    boxShadow: 'unset',
    px: '8px',
    gap: '8px',
    color: textColor,
    '&:hover': {
      background: isDarkMode ? palette.gray[800] : palette.gray[200],
      boxShadow: 'unset',
    },
  };

  return (
    <>
      <input
        type='file'
        id={`upload-file-beta-${spaceId ?? reactId}`}
        hidden
        ref={uploadFileRef}
        onChange={handleUpload}
      />

      <CollapsibleFolderGroup
        title='Uploads'
        defaultExpanded={false}
        icon={<Upload size={16} color={textColor} />}
        action={addButton}
      >
        {isUploading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 8px 4px 24px',
            }}
          >
            <CircularProgress size={14} />
            <Typography
              sx={{
                color: iconColor,
                fontFamily: 'Roboto',
                fontSize: '12px',
                fontStyle: 'italic',
              }}
            >
              Uploading...
            </Typography>
          </Box>
        )}

        {docs.map(doc => (
          <Item
            key={doc.id}
            icon={<File size={16} />}
            label={doc.name}
            isNested
            isMenuOpen={Boolean(anchorEl && activeDoc?.id === doc.id)}
            onClick={() => handlePreview(doc.src, doc.name, doc.id)}
            onMenuClick={anchor => openPopover(anchor, doc.id)}
          />
        ))}

        {!isUploading && docs.length === 0 && (
          <Typography
            sx={{
              color: iconColor,
              fontFamily: 'Roboto',
              fontSize: '12px',
              fontStyle: 'italic',
              padding: '4px 8px 4px 24px',
            }}
          >
            No files uploaded
          </Typography>
        )}
      </CollapsibleFolderGroup>

      <Popover
        open={Boolean(anchorEl) && Boolean(activeDoc)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { marginLeft: '8px' } } }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: '8px',
            background: isDarkMode ? palette.gray[900] : '#FFFFFF',
            minWidth: '180px',
          }}
        >
          {activeDoc && (
            <>
              <Button
                variant='contained'
                sx={popoverButtonSx}
                onClick={() =>
                  handlePreview(activeDoc.src, activeDoc.name, activeDoc.id)
                }
              >
                <Eye size={20} color={iconColor} />
                <Typography sx={{ color: textColor }}>View</Typography>
              </Button>
              <Button
                variant='contained'
                sx={popoverButtonSx}
                onClick={() => handleDownload(activeDoc.src, activeDoc.name)}
              >
                <Download size={20} color={iconColor} />
                <Typography sx={{ color: textColor }}>Download</Typography>
              </Button>
              <Button
                variant='contained'
                sx={{
                  ...popoverButtonSx,
                  color: '#b91c1c',
                  '&:hover': {
                    background: isDarkMode ? '#FFADB7' : '#fecdd3',
                    boxShadow: 'unset',
                  },
                }}
                onClick={() => handleDelete(activeDoc.id)}
              >
                <Trash size={20} color='#b91c1c' />
                <Typography sx={{ color: '#b91c1c' }}>Delete</Typography>
              </Button>
            </>
          )}
        </Box>
      </Popover>

      <FilesModal
        onHide={() => setPreview(initialPreview)}
        show={preview.isOpen}
        onOpenPreviewFile={preview}
      />
    </>
  );
};
